import { ChevronLeft, ChevronRight } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import {
  buildCalendarMonthGrid,
  type CalendarDayTone,
  calendarDateKey,
  calendarDayToneClass,
} from "@/shared/components/calendar/calendar-day";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/cn";

type CalendarSize = "sm" | "lg";
interface Props {
  /// Dates to dot/tint (past meetings, prior events, etc.). Brand-soft.
  markedDates?: ReadonlyArray<Date>;
  /// The "next / featured" date for read-only highlights.
  highlightDate?: Date | null;
  /// Picker mode selected date. Pair with `onSelect` to make each day a button.
  selectedDate?: Date | null;
  /// Called when the user clicks a day in picker mode.
  onSelect?: (date: Date) => void;
  /// Return true to disable a day. Useful for min/max constraints.
  disabledDate?: (date: Date) => boolean;
  dayTone?: (date: Date) => CalendarDayTone | undefined;
  dayTitle?: (date: Date) => string | undefined; // hover + reader text, e.g. a holiday name
  /// Initial month, defaults to the selected/highlight date or today's month.
  initialMonth?: Date;
  /// Whether to render the prev/next month chevrons. Default `true`.
  showMonthNav?: boolean;
  /// Shared size variant. `lg` is used by interactive date pickers.
  size?: CalendarSize;
  className?: string;
}
const WEEKDAY_HEADERS: ReadonlyArray<string> = ["M", "T", "W", "T", "F", "S", "S"];
/// Shared token-based month grid for read-only markers and interactive date picking.
export function Calendar({
  markedDates = [],
  highlightDate = null,
  selectedDate = null,
  onSelect,
  disabledDate,
  dayTone,
  dayTitle,
  initialMonth,
  showMonthNav = true,
  size = "sm",
  className,
}: Props): ReactNode {
  const today = useMemo(() => new Date(), []);
  const [month, setMonth] = useState<Date>(() => {
    const anchor = initialMonth ?? selectedDate ?? highlightDate ?? today;
    return new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  });
  useEffect(() => {
    const anchor = selectedDate ?? highlightDate;
    if (!anchor) return;
    setMonth((prev) =>
      prev.getFullYear() === anchor.getFullYear() && prev.getMonth() === anchor.getMonth()
        ? prev
        : new Date(anchor.getFullYear(), anchor.getMonth(), 1),
    );
  }, [selectedDate, highlightDate]);
  const dayCells = useMemo(() => buildCalendarMonthGrid(month), [month]);
  const markedKey = useMemo(() => new Set(markedDates.map(calendarDateKey)), [markedDates]);
  const highlightKey = highlightDate ? calendarDateKey(highlightDate) : null;
  const selectedKey = selectedDate ? calendarDateKey(selectedDate) : null;
  const todayKey = calendarDateKey(today);
  const monthKeyPrefix = `${month.getFullYear()}-${month.getMonth()}`;
  const monthLabel = month.toLocaleString(undefined, { month: "long", year: "numeric" });
  const isLarge = size === "lg";
  return (
    <div
      className={cn(
        "flex flex-col gap-3",
        isLarge && "w-80 gap-0 overflow-hidden rounded-lg border bg-card shadow-md",
        className,
      )}
    >
      <div
        className={cn(
          "flex items-center justify-between",
          isLarge && "border-b bg-surface-subtle px-3 py-2.5",
        )}
      >
        <span
          className={cn(
            "font-medium text-text-primary",
            isLarge ? "text-sm font-semibold" : "text-sm",
          )}
        >
          {monthLabel}
        </span>
        {showMonthNav ? (
          <div className="flex gap-1">
            <Button
              type="button"
              size="icon"
              variant={isLarge ? "outline" : "ghost"}
              className={isLarge ? "size-8" : "size-6"}
              aria-label="Previous month"
              onClick={() => setMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
            >
              <ChevronLeft aria-hidden="true" className={isLarge ? "size-4" : "size-3.5"} />
            </Button>
            <Button
              type="button"
              size="icon"
              variant={isLarge ? "outline" : "ghost"}
              className={isLarge ? "size-8" : "size-6"}
              aria-label="Next month"
              onClick={() => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
            >
              <ChevronRight aria-hidden="true" className={isLarge ? "size-4" : "size-3.5"} />
            </Button>
          </div>
        ) : null}
      </div>
      <div
        className={cn(
          "grid grid-cols-7 text-center font-medium text-text-quaternary",
          isLarge ? "gap-1 px-3 pt-3 text-2xs uppercase tracking-wide" : "gap-0.5 text-3xs",
        )}
        aria-hidden="true"
      >
        {WEEKDAY_HEADERS.map((d, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: reason: weekday labels repeat (T twice), index disambiguates and order never changes
          <span key={`wd-${d}-${i}`}>{d}</span>
        ))}
      </div>
      <div className={cn("grid grid-cols-7", isLarge ? "gap-1 px-3 pb-3 pt-1" : "gap-0.5")}>
        {dayCells.map((cell, i) =>
          renderCell({
            cell,
            slotIndex: i,
            monthKeyPrefix,
            selectedKey,
            highlightKey,
            todayKey,
            markedKey,
            onSelect,
            disabledDate,
            dayTone,
            dayTitle,
            size,
          }),
        )}
      </div>
    </div>
  );
}
interface CellArgs {
  cell: Date | null;
  slotIndex: number;
  monthKeyPrefix: string;
  selectedKey: string | null;
  highlightKey: string | null;
  todayKey: string;
  markedKey: Set<string>;
  onSelect?: (date: Date) => void;
  disabledDate?: (date: Date) => boolean;
  dayTone?: (date: Date) => CalendarDayTone | undefined;
  dayTitle?: (date: Date) => string | undefined;
  size: CalendarSize;
}
function renderCell(args: CellArgs): ReactNode {
  const { cell, slotIndex, monthKeyPrefix } = args;
  if (!cell) {
    return (
      <span
        key={`${monthKeyPrefix}-pad-${slotIndex}`}
        aria-hidden="true"
        className="aspect-square"
      />
    );
  }
  const key = calendarDateKey(cell);
  const isSelected = key === args.selectedKey;
  const isHighlight = key === args.highlightKey;
  const isToday = key === args.todayKey;
  const isMarked = args.markedKey.has(key);
  const isFilled = isHighlight;
  const isDisabled = args.disabledDate?.(cell) ?? false;
  const interactive = Boolean(args.onSelect);
  const tone = args.dayTone?.(cell);
  const customTitle = args.dayTitle?.(cell);
  const baseClass = cn(
    "relative flex aspect-square items-center justify-center font-medium tabular-nums",
    args.size === "lg" ? "rounded-md text-sm" : "rounded-md text-xs",
    tone
      ? calendarDayToneClass(tone)
      : isFilled
        ? "bg-primary text-primary-foreground shadow-xs"
        : isMarked
          ? "bg-brand-soft-bg text-text-brand-strong ring-1 ring-brand-soft-border"
          : isToday
            ? "text-text-brand-strong ring-1 ring-brand-soft-border"
            : "text-text-secondary",
    isSelected && "ring-2 ring-primary ring-offset-2 ring-offset-background",
    interactive && !isDisabled && "hover:bg-surface-subtle hover:ring-1 hover:ring-border",
    isDisabled && "cursor-not-allowed opacity-40",
  );
  const dot =
    isMarked && !isFilled && !tone ? (
      <span
        aria-hidden="true"
        className={cn(
          "absolute bottom-0.5 rounded-full bg-text-brand-strong",
          args.size === "lg" ? "size-1.5" : "size-1",
        )}
      />
    ) : null;
  if (interactive) {
    return (
      <button
        type="button"
        key={key}
        disabled={isDisabled}
        aria-pressed={isSelected}
        title={customTitle}
        aria-label={customTitle && `${cell.getDate()}, ${customTitle}`}
        onClick={() => args.onSelect?.(cell)}
        className={cn(baseClass, "focus-visible:outline-none focus-visible:shadow-ring-focus")}
      >
        {cell.getDate()}
        {dot}
      </button>
    );
  }
  return (
    <span
      key={key}
      title={
        customTitle ??
        (isHighlight
          ? "Upcoming"
          : isMarked
            ? "Past event on this day"
            : isToday
              ? "Today"
              : undefined)
      }
      className={baseClass}
    >
      {cell.getDate()}
      {dot}
    </span>
  );
}
